import { Injectable } from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { take } from 'rxjs/operators';
import {
  AuthService,
  FollowingService,
  GroupService,
} from '@dg/shared-services';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfileOverviewService } from '@app/profile/components/profile-overview/profile-overview.service';
import { Group } from '@app/groups/group-api';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@app/user/user-api.model';

export interface FollowUser extends User {
  submitting: boolean;
}

export interface SocialModalVM {
  followers: FollowUser[];
  followees: FollowUser[];
  groups: Group[];
  // separate from groups as we need to get this independently for the groups badge before we get the details of the groups
  groupsCount: number;
  followersCount: number;
  followeesCount: number;
  followingLabel: string;
  followersLabel: string;
  groupsLabel: string;
}

export interface StatusVM {
  loading: boolean;
  groupSubmittingId: number | undefined;
}

@Injectable({ providedIn: 'root' })
export class ProfileSocialModalFacade extends SubscriberBaseDirective {
  private _vm$: BehaviorSubject<SocialModalVM>;
  private _status$: BehaviorSubject<StatusVM>;

  public constructor(
    private followingService: FollowingService,
    private profileOverviewService: ProfileOverviewService,
    private groupService: GroupService,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    super();

    this._vm$ = new BehaviorSubject<SocialModalVM>({
      followers: [],
      followees: [],
      groups: [],
      followersCount: 0,
      followeesCount: 0,
      groupsCount: 0,
      followingLabel: '',
      followersLabel: '',
      groupsLabel: '',
    });

    this._status$ = new BehaviorSubject<StatusVM>({
      loading: true,
      groupSubmittingId: undefined,
    });

    if (this.authService.authUser) {
      this.followingService
        .getViewersFollowees(true)
        .pipe(this.takeUntilDestroyed())
        .subscribe();
    }

    this.followingService.followingChange
      .pipe(this.takeUntilDestroyed())
      .subscribe((...args) => {
        const followersCount = this.followingService.getFollowerCount();
        const followeesCount = this.followingService.getFolloweeCount();

        const followingLabel = this.translate.instant(
          'UserInfoPartial_FollowingCountFormat',
          {
            followeesCount,
          }
        );
        const key =
          followersCount === 1
            ? 'dgPathwaysInsights_FollowerCountFormat'
            : 'dgPathwaysInsights_FollowerCountPluralFormat';

        const followersLabel = this.translate.instant(key, {
          count: followersCount,
        });

        this.updateVM({
          followersCount,
          followeesCount,
          followingLabel,
          followersLabel,
        });
      });
  }

  public get vm$(): Observable<SocialModalVM> {
    return this._vm$.asObservable();
  }

  public get status$(): Observable<StatusVM> {
    return this._status$.asObservable();
  }

  public initCounts(userProfileKey: number): void {
    this.getFollowCounts(userProfileKey);
    this.getGroupsCount(userProfileKey);
  }

  public getFollowCounts(userProfileKey: number): void {
    this.followingService.loadFollowCounts(userProfileKey);
  }

  public loadFollowees(userProfileKey: number): void {
    this.updateStatus({ loading: true });
    this.followingService
      .getFollowees(userProfileKey)
      .pipe(take(1))
      .subscribe((followees) => {
        this.updateVM({ followees: followees.items });
        this.updateStatus({ loading: false });
      });
  }

  public loadFollowers(userProfileKey: number): void {
    this.updateStatus({ loading: true });
    this.followingService
      .getFollowers(userProfileKey)
      .pipe(take(1))
      .subscribe((followers) => {
        this.updateVM({ followers: followers.items });
        this.updateStatus({ loading: false });
      });
  }

  public setFollowUser({ isActive }, follower: FollowUser): void {
    follower.submitting = true;

    const action = isActive
      ? this.followingService.unfollowUser.bind(this.followingService)
      : this.followingService.followUser.bind(this.followingService);

    action(
      follower?.userProfile.userProfileId,
      follower?.userProfile.name,
      follower?.userProfile.isEngaged,
      false
    ).subscribe(() => {
      follower.submitting = false;
    });
  }

  public isViewersFollowee(userId: string): boolean {
    return this.followingService.isViewersFollowee(userId);
  }

  public loadGroups(userProfileKey: number, showLoading: boolean): void {
    if (showLoading) {
      this.updateStatus({ loading: true });
    }
    this.profileOverviewService
      .getUserGroups(userProfileKey)
      .pipe(take(1))
      .subscribe((groups) => {
        this.updateVM({ groups });
        if (showLoading) {
          this.updateStatus({ loading: false });
        }
      });
  }

  public joinGroup(group, ownerKey: number): void {
    this.updateStatus({ groupSubmittingId: group.groupId });
    this.groupService
      .getGroup(group.groupId)
      .pipe(take(1))
      .subscribe((fullGroup) => {
        const { groupId, name, userCount, privacyLevel } = group;
        this.groupService
          .join(
            { groupId, name, userCount, privacyLevel, interestNames: '' },
            '',
            false
          )
          .pipe(take(1))
          .subscribe(() => {
            this.loadGroups(ownerKey, false);
            this.getGroupsCount(ownerKey);
            this.updateStatus({ groupSubmittingId: undefined });
          });
      });
  }

  public leaveGroup(group, ownerKey: number): void {
    this.updateStatus({ groupSubmittingId: group.groupId });
    this.groupService
      .leave(group, false)
      .pipe(take(1))
      .subscribe(() => {
        this.loadGroups(ownerKey, false);
        this.getGroupsCount(ownerKey);
        this.updateStatus({ groupSubmittingId: undefined });
      });
  }

  public requestGroupMembership(group): void {
    this.updateStatus({ groupSubmittingId: group.groupId });
    group.isPendingMember = true; // Assume success - this is consistent with requesting group membership from the groups page
    this.groupService
      .requestMembership(group)
      .pipe(take(1))
      .subscribe(() => {
        this.updateVM({});
        this.updateStatus({ groupSubmittingId: undefined });
      });
  }

  public getGroupsCount(userProfileKey: number): void {
    this.profileOverviewService
      .getUserGroupsCount(userProfileKey)
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ groupsCount }) => {
        const groupsLabel = this.translate.instant(
          'dgOrgOverview_GroupCountFormat',
          {
            count: groupsCount,
          }
        );
        this.updateVM({ groupsCount, groupsLabel });
      });
  }

  private updateVM(args): void {
    this._vm$.next({
      ...this._vm$.value,
      ...args,
    });
  }

  private updateStatus(args?): void {
    if (args) {
      this._status$.next({ ...this._status$.value, ...args });
    } else {
      this._status$.next({
        loading: false,
        groupSubmittingId: undefined,
      });
    }
  }
}
